import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { Confirmation, ToasterService } from '@abp/ng.theme.shared';
import { Component, DestroyRef, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnButton,
  ZnConfirmation,
  ZnPill,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import { StandFusionStandDetailDto } from 'orchestrator';
import { firstValueFrom, of } from 'rxjs';

import type { IFusionStand } from '../interfaces/IFusionStand';
import type { IStepSelectEstablishmentData } from '../interfaces/IStepSelectEstablishmentData';
import type { IStepSelectStandsToFuseData } from '../interfaces/IStepSelectStandsToFuseData';
import { StandFusionPlanningService } from '../services/stand-fusion-planning.service';

type SelectValue = string | string[] | null;

const EMPTY_VALUE = '-----';

@Component({
  selector: 'lib-step-select-stands-to-fuse',
  standalone: true,
  imports: [LocalizationPipe, ZnButton, ZnPill, ZnSingleSelect],
  templateUrl: './step-select-stands-to-fuse.html',
})
export class StepSelectStandsToFuse
  implements WizardStepComponent<IStepSelectStandsToFuseData>
{
  readonly standSelect = viewChild('standSelect', { read: ZnSingleSelect });

  private readonly toaster = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly standFusionService = inject(StandFusionPlanningService);
  private readonly destroyRef = inject(DestroyRef);

  readonly localizationKeyPrefix = 'Geographic::StandFusion.StepSelectStandsToFuse';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;
  readonly previousStepId = 1;

  readonly establishmentId = signal<string | null>(null);
  readonly establishmentName = signal<string | null>(null);
  readonly selectedStandId = signal<string | null>(null);
  readonly selectedStands = signal<IFusionStand[]>([]);
  readonly loading = signal(false);

  readonly totalArea = computed(() =>
    this.roundArea(this.selectedStands().reduce((sum, row) => sum + row.area, 0)),
  );
  readonly persistedStand = computed(
    () => this.selectedStands().find(row => row.isPersistedStand) ?? null,
  );
  readonly summarySpecies = computed(() => this.persistedStand()?.species ?? null);
  readonly summaryLandContract = computed(() => this.persistedStand()?.landContract ?? null);
  readonly summaryForestContract = computed(() => this.persistedStand()?.forestContract ?? null);
  readonly addDisabled = computed(() => !this.selectedStandId() || this.loading());

  readonly standOptionsLoader = (params?: ZnSelectQueryParams) => {
    const propertyId = this.numericEstablishmentId();
    if (!propertyId) {
      return of({ items: [], totalCount: 0 });
    }

    return this.standFusionService.searchStandOptions(propertyId, params);
  };

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const previousStepData =
      context.getStepState<IStepSelectEstablishmentData>(this.previousStepId)?.data;
    const currentState =
      context.getStepState<IStepSelectStandsToFuseData>(context.activeStepId())?.data;

    const establishmentId = previousStepData?.establishmentId ?? currentState?.establishmentId ?? null;
    this.establishmentId.set(establishmentId);
    this.establishmentName.set(
      previousStepData?.establishmentName ?? currentState?.establishmentName ?? null,
    );
    this.selectedStandId.set(null);

    if (currentState && currentState.establishmentId === establishmentId) {
      this.selectedStands.set(currentState.selectedStands ?? []);
    } else {
      this.selectedStands.set([]);
    }

    queueMicrotask(() => {
      this.standSelect()?.writeValue(null);
    });
  }

  onStandChange(value: SelectValue): void {
    this.selectedStandId.set(this.toSingleSelectValue(value));
  }

  async addStand(): Promise<void> {
    if (this.loading()) {
      return;
    }

    const propertyId = this.numericEstablishmentId();
    const standId = this.numericSelectedStandId();
    if (!propertyId || !standId) {
      this.notifyError('SelectionRequired');
      return;
    }

    if (this.selectedStands().some(row => row.backendId === standId)) {
      this.notifyError('StandAlreadySelected');
      this.clearStandSelection();
      return;
    }

    this.loading.set(true);

    try {
      const detail = await firstValueFrom(
        this.standFusionService.validateStand(
          propertyId,
          standId,
          this.persistedStand()?.backendId ?? null,
        ),
      );
      const stand = this.toFusionStand(detail);
      if (!stand) {
        return;
      }

      this.selectedStands.update(rows => [...rows, stand]);
      this.clearStandSelection();
    } catch {
      // Business validation messages are returned and localized by the Orchestrator/ABP pipeline.
    } finally {
      this.loading.set(false);
    }
  }

  removeStand(row: IFusionStand): void {
    this.confirmationService
      .showCancelConfirmation({
        messageKey: `${this.localizationKeyPrefix}.RemoveConfirmationMessage`,
        titleKey: `${this.localizationKeyPrefix}.RemoveConfirmationTitle`,
        type: 'error',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(status => {
        if (status !== Confirmation.Status.confirm) {
          return;
        }

        if (row.isPersistedStand) {
          this.selectedStands.set([]);
          this.clearStandSelection();
          return;
        }

        this.selectedStands.update(rows => rows.filter(item => item.backendId !== row.backendId));
      });
  }

  canLeave(): boolean {
    if (this.selectedStands().length >= 2 && !!this.persistedStand()) {
      return true;
    }

    this.notifyError('MinimumSelectionRequired');
    return false;
  }

  canLeaveSync(): boolean {
    return !this.loading();
  }

  hasMeaningfulData(): boolean {
    return this.selectedStands().length > 0;
  }

  collect(context: IWizardContext): IStepSelectStandsToFuseData {
    const persistedStand = this.persistedStand();
    const data: IStepSelectStandsToFuseData = {
      establishmentId: this.establishmentId(),
      establishmentName: this.establishmentName(),
      selectedStands: this.selectedStands(),
      resultingStand: persistedStand
        ? {
            standId: persistedStand.id,
            standBackendId: persistedStand.backendId,
            standCycleRotationId: persistedStand.standCycleRotationId,
            standName: persistedStand.name,
            cycleId: persistedStand.cycleId,
            cycle: persistedStand.cycle,
            isLastCycle: persistedStand.isLastCycle,
            rotationId: persistedStand.rotationId,
            rotation: persistedStand.rotation,
            isLastRotation: persistedStand.isLastRotation,
            currentArea: persistedStand.area,
            adjustedArea: this.totalArea(),
            speciesId: persistedStand.speciesId,
            species: persistedStand.species,
            landContractId: persistedStand.landContractId,
            landContract: persistedStand.landContract,
            forestContractId: persistedStand.forestContractId,
            forestContract: persistedStand.forestContract,
          }
        : null,
      totalArea: this.totalArea(),
    };

    context.patchStepState(context.activeStepId(), {
      visited: true,
      valid: this.selectedStands().length >= 2 && !!persistedStand,
      completed: true,
      data,
    });

    return data;
  }

  formatArea(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return EMPTY_VALUE;
    }

    return value.toFixed(2).replace('.', ',');
  }

  displayValue(value: string | null | undefined): string {
    return value?.trim() ? value : EMPTY_VALUE;
  }

  formatCycleLabel(value: string | null | undefined, isLastCycle: boolean): string {
    return this.formatCycleOrRotationLabel(
      value,
      isLastCycle,
      'Geographic::Entity.Cycle.IsLastCycle',
    );
  }

  formatRotationLabel(value: string | null | undefined, isLastRotation: boolean): string {
    return this.formatCycleOrRotationLabel(
      value,
      isLastRotation,
      'Geographic::Entity.Rotation.IsLastRotation',
    );
  }

  rowResultLabelKey(row: IFusionStand): string {
    return `${this.localizationKeyPrefix}.Table.Results.${row.isPersistedStand ? 'Persist' : 'Deactivate'}`;
  }

  private toFusionStand(detail: StandFusionStandDetailDto): IFusionStand | null {
    const backendId = detail.id;
    if (!backendId) {
      return null;
    }

    return {
      id: String(backendId),
      backendId,
      locationId: detail.locationId ?? null,
      establishmentId: String(detail.propertyId ?? this.establishmentId() ?? ''),
      name: detail.name ?? String(backendId),
      area: detail.standArea ?? 0,
      standType: this.displayValue(detail.standTypeName),
      forestrySituation: this.displayValue(detail.forestrySituationName),
      cycleId: detail.cycleId ?? null,
      cycle: this.formatNumericValue(detail.cycleValue),
      isLastCycle: !!detail.isLastCycle,
      rotationId: detail.rotationId ?? null,
      rotation: this.formatNumericValue(detail.rotationValue),
      isLastRotation: !!detail.isLastRotation,
      rotationUseId: detail.rotationUseId ?? null,
      rotationUse: this.displayValue(detail.rotationUseName),
      plantationId: detail.plantation?.id ?? null,
      plantationDate: this.formatDate(detail.plantation?.plantationDate),
      ownPlantation: this.formatBooleanValue(detail.plantation?.isOwnPlantation),
      geneticMaterialId: detail.plantation?.geneticMaterialId ?? null,
      geneticMaterial: this.displayValue(detail.plantation?.geneticMaterialName),
      speciesId: detail.plantation?.speciesId ?? null,
      species: this.displayValue(detail.plantation?.speciesName),
      standCycleRotationId: detail.cycleRotation?.standCycleRotationId ?? null,
      landContractId: detail.cycleRotation?.landContractId ?? null,
      landContract: this.displayValue(detail.cycleRotation?.landContractNumber),
      forestContractId: detail.cycleRotation?.fomentContractId ?? null,
      forestContract: this.displayValue(detail.cycleRotation?.fomentContractNumber),
      isPersistedStand: !!detail.isPersistedStand,
      detail,
    };
  }

  private numericEstablishmentId(): number | null {
    const value = Number(this.establishmentId());
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  private numericSelectedStandId(): number | null {
    const value = Number(this.selectedStandId());
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  private toSingleSelectValue(value: SelectValue): string | null {
    if (Array.isArray(value)) {
      return value[0] ?? null;
    }

    return value || null;
  }

  private clearStandSelection(): void {
    this.selectedStandId.set(null);
    queueMicrotask(() => {
      this.standSelect()?.writeValue(null);
    });
  }

  private formatNumericValue(value: number | null | undefined): string {
    return this.formatOptionalValue(value, String);
  }

  private formatBooleanValue(value: boolean | null | undefined): string {
    return this.formatOptionalValue(value, booleanValue =>
      this.localizationService.instant(booleanValue ? 'AbpUi::Yes' : 'AbpUi::No'),
    );
  }

  private formatDate(value: string | null | undefined): string {
    return this.formatNonEmptyValue(value, dateValue => {
      const [year, month, day] = dateValue.split('-');
      if (!year || !month || !day) {
        return dateValue;
      }

      return `${day}/${month}/${year}`;
    });
  }

  private roundArea(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private formatCycleOrRotationLabel(
    value: string | null | undefined,
    isLastValue: boolean,
    suffixKey: string,
  ): string {
    const formattedValue = this.displayValue(value);
    if (!isLastValue) {
      return formattedValue;
    }

    return `${formattedValue} - ${this.localizationService.instant(suffixKey)}`;
  }

  private formatOptionalValue<T>(value: T | null | undefined, formatter: (value: T) => string): string {
    if (value === null || value === undefined) {
      return EMPTY_VALUE;
    }

    return formatter(value);
  }

  private formatNonEmptyValue(
    value: string | null | undefined,
    formatter: (value: string) => string,
  ): string {
    if (!value?.trim()) {
      return EMPTY_VALUE;
    }

    return formatter(value);
  }

  private notifyError(key: string): void {
    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandFusion.StepSelectStandsToFuse.${key}`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
  }
}
